// src/features/inspections/InspectionForm.jsx - OPTIMIZADO SIN CONSULTAS REPETIDAS
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Card, Row, Col, Spinner, Container, InputGroup } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaSearch } from 'react-icons/fa';
import dayjs from 'dayjs';

// Componentes y hooks
import { useNavigationHistory } from '../../hooks/useNavigationHistory';
import CustomSnackbar from '../../components/CustomSnackbar';

// Servicios
import InspectionsService from './InspectionsService';
import PeopleService from '../people/PeopleService';
import WorksService from '../works/WorksService';

// Constantes
import { 
  TIPOS_INSPECCIONES_EQUIPOS, 
  TIPOS_INSPECCIONES_PERSONAS 
} from '../../utils/constants';

// Constantes para la selección de responsables
const NEW_RESPONSABLE_OPTION_VALUE = '---CREATE_NEW---';
const NEW_RESPONSABLE_LABEL = 'Crear un nuevo responsable...';

// Componente SearchableDropdown reutilizable
const SearchableDropdown = ({ 
  items = [], 
  label, 
  value, 
  onChange, 
  placeholder = "Buscar...",
  disabled = false,
  isInvalid = false,
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredItems, setFilteredItems] = useState(items);
  const dropdownRef = React.useRef(null);

  // Filtrar items cuando cambia el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredItems(items);
      return;
    }

    const lowercasedFilter = searchTerm.toLowerCase();
    const filtered = items.filter(item => {
      // Personas (tienen firstName, surname)
      if (item.firstName || item.surname) {
        const fullName = `${item.firstName || ''} ${item.surname || ''}`.trim();
        return (
          fullName.toLowerCase().includes(lowercasedFilter) ||
          (item.document && item.document.toLowerCase().includes(lowercasedFilter))
        );
      }
      // Obras (tienen name)
      else if (item.name) {
        return item.name.toLowerCase().includes(lowercasedFilter);
      }
      // Equipos (tienen tool.name)
      else if (item.tool && item.tool.name) {
        return (
          item.tool.name.toLowerCase().includes(lowercasedFilter) ||
          (item.serial && item.serial.toLowerCase().includes(lowercasedFilter))
        );
      }
      return false;
    });
    
    setFilteredItems(filtered);
  }, [searchTerm, items]);

  // Actualizar items filtrados cuando cambian los items
  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Renderizar el valor seleccionado
  const renderSelectedValue = () => {
    if (!value) return '';

    // Si es una persona
    if (value.firstName || value.surname) {
      const firstName = (value.firstName || '').trim();
      const surname = (value.surname || '').trim();
      const document = value.document ? ` - ${value.document}` : '';
      
      if (firstName || surname) {
        return `${firstName} ${surname}${document}`.trim();
      }
      return document ? document.substring(3) : 'Persona sin nombre';
    }
    // Si es una obra
    else if (value.name) {
      return value.name;
    }
    // Si es un equipo
    else if (value.tool && value.tool.name) {
      const toolName = value.tool.name;
      const serial = value.serial ? ` - Serial: ${value.serial}` : '';
      return `${toolName}${serial}`;
    }

    return 'Elemento seleccionado';
  };

  return (
    <div className="position-relative" ref={dropdownRef}>
      <Form.Label>{label} {required && <span className="text-danger">*</span>}</Form.Label>
      <InputGroup>
        <Form.Control
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={() => setShowDropdown(true)}
          disabled={disabled}
          isInvalid={isInvalid}
        />
        <InputGroup.Text>
          <FaSearch />
        </InputGroup.Text>
      </InputGroup>
      {showDropdown && filteredItems.length > 0 && (
        <div 
          className="position-absolute w-100 border rounded mt-1 bg-white shadow-sm" 
          style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}
        >
          {filteredItems.map((item, index) => {
            // Determinar qué tipo de item es y crear texto apropiado
            let itemText = '';
            
            // Personas
            if (item.firstName || item.surname) {
              const fullName = `${item.firstName || ''} ${item.surname || ''}`.trim();
              const document = item.document ? ` - ${item.document}` : '';
              itemText = `${fullName}${document}`;
            } 
            // Obras
            else if (item.name) {
              itemText = item.name;
            } 
            // Equipos
            else if (item.tool && item.tool.name) {
              const serial = item.serial ? ` - Serial: ${item.serial}` : '';
              itemText = `${item.tool.name}${serial}`;
            }
            
            return (
              <div 
                key={index}
                className="p-2 border-bottom hover-bg-light"
                onClick={() => {
                  onChange(item);
                  setShowDropdown(false);
                  setSearchTerm('');
                }}
                style={{ cursor: 'pointer' }}
              >
                {itemText}
              </div>
            );
          })}
        </div>
      )}
      {value && (
        <div className="mt-2 p-2 border rounded bg-light">
          <strong>Inspeccionado:</strong> {renderSelectedValue()}
        </div>
      )}
    </div>
  );
};

function InspectionForm() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory();
  const isEditMode = Boolean(id);
  
  // Estados
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  
  // Estado del formulario
  const [inspectionType, setInspectionType] = useState('');
  const [ownerType, setOwnerType] = useState('person');
  
  // Listas de datos
  const [people, setPeople] = useState([]);
  const [works, setWorks] = useState([]);
  const [tools, setTools] = useState([]);
  
  // Estados para responsables
  const [responsableOptions, setResponsableOptions] = useState([]);
  const [loadingResponsables, setLoadingResponsables] = useState(true);
  const [isCreatingNewResponsable, setIsCreatingNewResponsable] = useState(false);
  
  // Selecciones
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  
  // Datos del formulario
  const [formData, setFormData] = useState({
    type: '',
    date: dayjs().format('YYYY-MM-DD'),
    responsable: '',
    toolStatus: '',
    observations: '',
    finished: false
  });
  
  // Estado de validación
  const [errors, setErrors] = useState({});

  // ✅ OPTIMIZADO: Cargar responsables UNA SOLA VEZ al montar el componente
  useEffect(() => {
    const fetchResponsables = async () => {
      try {
        setLoadingResponsables(true);
        const names = await InspectionsService.getResponsables();
        
        if (Array.isArray(names)) {
          let nameList = [];
          
          // Procesar cada elemento de manera flexible
          names.forEach(item => {
            let name = null;
            
            if (Array.isArray(item)) {
              // Si es array, intentar tomar el segundo elemento
              name = item[1];
            } else if (typeof item === 'string') {
              // Si es string directamente
              name = item;
            } else if (item && typeof item === 'object' && item.responsable) {
              // Si es objeto con propiedad responsable
              name = item.responsable;
            }
            
            // Validar que el nombre sea válido antes de agregarlo
            if (name && typeof name === 'string' && name.trim() !== '') {
              nameList.push(name.trim());
            }
          });
          
          // Eliminar duplicados
          nameList = [...new Set(nameList)];
          
          setResponsableOptions(nameList);
        } else {
          console.error("La respuesta no es un array:", names);
          setResponsableOptions([]);
        }
      } catch (error) {
        console.error("Error al cargar responsables:", error);
        setResponsableOptions([]);
      } finally {
        setLoadingResponsables(false);
      }
    };
    
    fetchResponsables();
  }, []); // ✅ Sin dependencias - se ejecuta solo una vez

  // ✅ NUEVO: Verificar si el responsable actual existe en la lista (solo para modo edición)
  useEffect(() => {
    const checkExistingResponsable = () => {
      if (isEditMode && formData.responsable && responsableOptions.length > 0) {
        if (!responsableOptions.includes(formData.responsable)) {
          setIsCreatingNewResponsable(true);
        }
      }
    };
    
    checkExistingResponsable();
  }, [isEditMode, formData.responsable, responsableOptions]); // Solo cuando cambian estas variables específicas

  // Manejar selección de responsable
  const handleResponsableSelection = (e) => {
    const { value } = e.target;
    if (value === NEW_RESPONSABLE_OPTION_VALUE) {
      // Primera alerta al seleccionar la opción de crear
      window.alert("Verifique que el responsable que requiere no esta contemplado en la lista");
      
      setIsCreatingNewResponsable(true);
      setFormData(prev => ({ ...prev, responsable: '' }));
    } else {
      setIsCreatingNewResponsable(false);
      setFormData(prev => ({ ...prev, responsable: value }));
    }
    if (errors.responsable) {
      setErrors(prev => ({ ...prev, responsable: false }));
    }
  };

  // Cargar personas
  const loadPeople = useCallback(async () => {
    try {
      setLoadingData(true);
      const data = await PeopleService.getAllPeople();
      const filteredPeople = data.filter(person => person.type === 'F');
      setPeople(filteredPeople);
    } catch (error) {
      console.error('Error al cargar personas:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar la lista de personas'
      });
    } finally {
      setLoadingData(false);
    }
  }, []);
  
  // Cargar obras
  const loadWorks = useCallback(async () => {
    try {
      setLoadingData(true);
      const data = await WorksService.getAllWorks();
      const activeWorks = data.filter(work => !work.deletedDate);
      setWorks(activeWorks);
    } catch (error) {
      console.error('Error al cargar obras:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar la lista de obras'
      });
    } finally {
      setLoadingData(false);
    }
  }, []);
  
  // Cargar herramientas para un dueño específico
  const loadToolsForOwner = useCallback(async (owner, ownerType) => {
    try {
      setLoadingData(true);
      
      if (!owner) {
        setTools([]);
        return;
      }
      
      if (ownerType === 'person') {
        const personData = await PeopleService.getPerson(owner.id);
        setTools(personData.tools || []);
      } else {
        const workData = await WorksService.getWork(owner.id);
        setTools(workData.tools || []);
      }
    } catch (error) {
      console.error('Error al cargar herramientas:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar las herramientas asignadas'
      });
    } finally {
      setLoadingData(false);
    }
  }, []);
  
  // FUNCIÓN LOADINSPECTION CORREGIDA - SIN DUPLICACIÓN
  const loadInspection = useCallback(async (inspectionId) => {
    try {
      setLoadingData(true);
      const data = await InspectionsService.getInspection(inspectionId);
      
      setFormData({
        type: data.type || '',
        date: data.date ? dayjs(data.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        responsable: data.responsable || '',
        toolStatus: data.toolStatus || '',
        observations: data.observations || '',
        finished: data.finished || false
      });
      
      // Caso 1: Inspección directa de persona (person existe, assignedTool es null)
      if (data.person && !data.assignedTool) {
        setInspectionType('person');
        
        // Cargar personas
        const peopleData = await PeopleService.getAllPeople();
        const filteredPeople = peopleData.filter(person => person.type === 'F');
        setPeople(filteredPeople);
        
        setSelectedOwner({
          id: data.person.id,
          firstName: data.person.firstName || '',
          surname: data.person.surname || '',
          document: data.person.document || '',
          ...data.person
        });
      } 
      // Caso 2: Inspección de herramienta (assignedTool existe)
      else if (data.assignedTool) {
        setInspectionType('tool');
        
        setSelectedTool({
          id: data.assignedTool.id,
          tool: data.assignedTool.tool || {},
          serial: data.assignedTool.serial || '',
          ...data.assignedTool
        });
        
        // Subcaso 2a: Herramienta asignada a una persona
        if (data.assignedTool.person) {
          setOwnerType('person');
          
          // Cargar personas
          const peopleData = await PeopleService.getAllPeople();
          const filteredPeople = peopleData.filter(person => person.type === 'F');
          setPeople(filteredPeople);
          
          setSelectedOwner({
            id: data.assignedTool.person.id,
            firstName: data.assignedTool.person.firstName || '',
            surname: data.assignedTool.person.surname || '',
            document: data.assignedTool.person.document || '',
            ...data.assignedTool.person
          });
          
          // Intentar cargar herramientas de esta persona
          try {
            const personData = await PeopleService.getPerson(data.assignedTool.person.id);
            setTools(personData.tools || []);
          } catch (error) {
            console.warn('No se pudieron cargar las herramientas de la persona:', error);
            setTools([data.assignedTool]);
          }
        } 
        // Subcaso 2b: Herramienta asignada a una obra
        else if (data.assignedTool.work) {
          setOwnerType('work');
          
          // Cargar obras
          const worksData = await WorksService.getAllWorks();
          const activeWorks = worksData.filter(work => !work.deletedDate);
          setWorks(activeWorks);
          
          setSelectedOwner({
            id: data.assignedTool.work.id,
            name: data.assignedTool.work.name || '',
            ...data.assignedTool.work
          });
          
          // Intentar cargar herramientas de esta obra
          try {
            const workData = await WorksService.getWork(data.assignedTool.work.id);
            setTools(workData.tools || []);
          } catch (error) {
            console.warn('No se pudieron cargar las herramientas de la obra:', error);
            setTools([data.assignedTool]);
          }
        } 
        // Subcaso 2c: Herramienta SIN información de asignación en la respuesta de inspección
        else {
          try {
            // Hacer llamada adicional para obtener información completa de la herramienta asignada
            const assignedToolResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/assigned-tools/${data.assignedTool.id}`, {
              headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (assignedToolResponse.ok) {
              const fullAssignedToolData = await assignedToolResponse.json();
              
              // Actualizar selectedTool con datos completos
              setSelectedTool({
                id: fullAssignedToolData.id,
                tool: fullAssignedToolData.tool || {},
                serial: fullAssignedToolData.serial || '',
                ...fullAssignedToolData
              });
              
              // Verificar si ahora tenemos información de asignación
              if (fullAssignedToolData.person) {
                setOwnerType('person');
                
                // Cargar personas
                const peopleData = await PeopleService.getAllPeople();
                const filteredPeople = peopleData.filter(person => person.type === 'F');
                setPeople(filteredPeople);
                
                setSelectedOwner({
                  id: fullAssignedToolData.person.id,
                  firstName: fullAssignedToolData.person.firstName || '',
                  surname: fullAssignedToolData.person.surname || '',
                  document: fullAssignedToolData.person.document || '',
                  ...fullAssignedToolData.person
                });
                
                // Cargar herramientas de esta persona
                try {
                  const personData = await PeopleService.getPerson(fullAssignedToolData.person.id);
                  setTools(personData.tools || []);
                } catch (error) {
                  console.warn('No se pudieron cargar las herramientas de la persona:', error);
                  setTools([fullAssignedToolData]);
                }
              } 
              else if (fullAssignedToolData.work) {
                setOwnerType('work');
                
                // Cargar obras
                const worksData = await WorksService.getAllWorks();
                const activeWorks = worksData.filter(work => !work.deletedDate);
                setWorks(activeWorks);
                
                setSelectedOwner({
                  id: fullAssignedToolData.work.id,
                  name: fullAssignedToolData.work.name || '',
                  ...fullAssignedToolData.work
                });
                
                // Cargar herramientas de esta obra
                try {
                  const workData = await WorksService.getWork(fullAssignedToolData.work.id);
                  setTools(workData.tools || []);
                } catch (error) {
                  console.warn('No se pudieron cargar las herramientas de la obra:', error);
                  setTools([fullAssignedToolData]);
                }
              } 
              else {
                // Realmente no tiene asignación
                setOwnerType('person');
                
                // Cargar listas por si el usuario quiere cambiar algo
                const peopleData = await PeopleService.getAllPeople();
                const filteredPeople = peopleData.filter(person => person.type === 'F');
                setPeople(filteredPeople);
                
                const worksData = await WorksService.getAllWorks();
                const activeWorks = worksData.filter(work => !work.deletedDate);
                setWorks(activeWorks);
                
                setSelectedOwner(null);
                setTools([fullAssignedToolData]);
                
                setSnackbar({
                  open: true,
                  type: 'info',
                  message: 'Esta herramienta no tiene una asignación específica a persona u obra.'
                });
              }
            } else {
              throw new Error(`Error ${assignedToolResponse.status}: ${assignedToolResponse.statusText}`);
            }
            
          } catch (error) {
            console.error('Error al obtener datos completos de assigned-tool:', error);
            
            // Fallback: tratar como herramienta sin asignación
            setOwnerType('person');
            
            const peopleData = await PeopleService.getAllPeople();
            const filteredPeople = peopleData.filter(person => person.type === 'F');
            setPeople(filteredPeople);
            
            const worksData = await WorksService.getAllWorks();
            const activeWorks = worksData.filter(work => !work.deletedDate);
            setWorks(activeWorks);
            
            setSelectedOwner(null);
            setTools([data.assignedTool]);
            
            setSnackbar({
              open: true,
              type: 'warning',
              message: 'No se pudo obtener información completa de asignación de la herramienta.'
            });
          }
        }
      } 
      // Caso 3: Datos incompletos o estructura inesperada
      else {
        console.error('Estructura de datos inesperada');
        setSnackbar({
          open: true,
          type: 'warning',
          message: 'La inspección tiene una estructura de datos inesperada. Algunos campos pueden no mostrarse correctamente.'
        });
        
        setInspectionType('person');
        const peopleData = await PeopleService.getAllPeople();
        const filteredPeople = peopleData.filter(person => person.type === 'F');
        setPeople(filteredPeople);
      }
      
    } catch (error) {
      console.error('Error al cargar los datos de la inspección:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar los datos de la inspección: ' + (error.message || 'Error desconocido')
      });
    } finally {
      setLoadingData(false);
    }
  }, []); // ✅ SIN DEPENDENCIAS para evitar loops

  // Cargar datos de la inspección si estamos en modo edición - OPTIMIZADO
  useEffect(() => {
    if (isEditMode && id) {
      loadInspection(id);
    }
  }, [isEditMode, id, loadInspection]);
  
  // Manejar cambio de tipo de inspección
  const handleInspectionTypeChange = useCallback((e) => {
    const type = e.target.value;
    setInspectionType(type);
    
    setSelectedOwner(null);
    setSelectedTool(null);
    setFormData(prev => ({
      ...prev,
      type: ''
    }));
    
    setErrors({});
    
    if (type === 'person') {
      loadPeople();
    } else if (type === 'tool') {
      loadPeople();
      loadWorks();
    }
  }, [loadPeople, loadWorks]);
  
  // Manejar cambio de tipo de dueño
  const handleOwnerTypeChange = useCallback((e) => {
    const type = e.target.value;
    setOwnerType(type);
    
    setSelectedOwner(null);
    setSelectedTool(null);
    
    if (type === 'person') {
      loadPeople();
    } else {
      loadWorks();
    }
  }, [loadPeople, loadWorks]);
  
  // Manejar selección de dueño
  const handleOwnerSelect = useCallback((owner) => {
    setSelectedOwner(owner);
    setSelectedTool(null);
    
    if (inspectionType === 'tool') {
      loadToolsForOwner(owner, ownerType);
    }
  }, [inspectionType, ownerType, loadToolsForOwner]);
  
  // Manejar selección de herramienta
  const handleToolSelect = useCallback((tool) => {
    setSelectedTool(tool);
  }, []);
  
  // Manejar cambios en los campos del formulario
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  }, [errors]);
  
  // Validar formulario - ACTUALIZADO
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!inspectionType) {
      newErrors.inspectionType = true;
    }
    
    if (!formData.type) newErrors.type = true;
    if (!formData.date) newErrors.date = true;
    if (!formData.responsable.trim()) newErrors.responsable = true;
    if (!formData.observations.trim()) newErrors.observations = true;
    
    // Validar selecciones según el tipo de inspección
    if (inspectionType === 'person') {
      // Para inspecciones de persona, siempre se requiere selectedOwner
      if (!selectedOwner) {
        newErrors.owner = true;
      }
    } else if (inspectionType === 'tool') {
      // Para inspecciones de herramienta, verificar los casos
      if (!selectedTool) {
        newErrors.tool = true;
      }
      
      // Solo requerir owner si NO es el caso 2c (herramienta sin asignación específica)
      // En modo edición, si hay selectedTool pero no selectedOwner, es el caso 2c válido
      const isCaso2c = isEditMode && selectedTool && !selectedOwner;
      
      if (!isCaso2c && !selectedOwner) {
        newErrors.owner = true;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [inspectionType, formData, selectedOwner, selectedTool, isEditMode]);
  
  // Manejar envío del formulario
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (loading) {
      console.warn('Ya se está enviando, ignorando clic');
      return;
    }
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    if (isCreatingNewResponsable) {
      // Segunda alerta de confirmación antes de enviar
      const confirmCreation = window.confirm("¿Está seguro que desea crear un nuevo responsable?");
      if (!confirmCreation) {
        return; // Detener si el usuario cancela
      }
    }
    
    try {
      setLoading(true);
      
      const inspectionData = {
        ...formData,
        // Limpiar espacios en blanco del responsable
        responsable: formData.responsable.trim(),
        date: dayjs(formData.date).format('YYYY-MM-DD')
      };
      
      let result;
      
      if (isEditMode) {
        result = await InspectionsService.updateInspection(id, inspectionData);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Inspección actualizada correctamente'
        });
      } else {
        if (inspectionType === 'person') {
          result = await InspectionsService.createPersonInspection(selectedOwner.id, inspectionData);
        } else {
          result = await InspectionsService.createToolInspection(selectedTool.id, inspectionData);
        }
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Inspección creada correctamente'
        });
      }
      
      setTimeout(() => {
        navigate('/inspections');
      }, 1500);
      
    } catch (error) {
      console.error('Error al guardar la inspección:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: `Error al ${isEditMode ? 'actualizar' : 'crear'} la inspección: ${error.message || 'Error desconocido'}`
      });
      
      setLoading(false);
    }
  }, [loading, validateForm, formData, isEditMode, inspectionType, selectedOwner, selectedTool, id, navigate, isCreatingNewResponsable]);
  
  // Volver a la lista de inspecciones
  const handleCancel = useCallback(() => {
    goBack('/inspections');
  }, [goBack]);

  // Función auxiliar para obtener el tipo de inspección
  const getInspectionTypes = useCallback(() => {
    return inspectionType === 'person' ? TIPOS_INSPECCIONES_PERSONAS : TIPOS_INSPECCIONES_EQUIPOS;
  }, [inspectionType]);
  
  return (
    <Container>
      <Card className="mb-4">
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="mb-0">
            {isEditMode ? 'Editar Inspección' : 'Nueva Inspección'}
          </h5>
        </Card.Header>
        
        <Card.Body className="px-4 py-3">
          {loading || loadingData ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
              <p className="mt-2">Cargando datos...</p>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              {/* Tipo de inspección (persona o herramienta) */}
              {!isEditMode && (
                <Row className="mb-3">
                  <Form.Group as={Col} sm={12} md={6}>
                    <Form.Label>Inspeccionado <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="inspectionType"
                      value={inspectionType}
                      onChange={handleInspectionTypeChange}
                      isInvalid={errors.inspectionType}
                      disabled={loading}
                    >
                      <option value="">Seleccione...</option>
                      <option value="person">Persona</option>
                      <option value="tool">Herramienta</option>
                    </Form.Select>
                    {errors.inspectionType && (
                      <Form.Control.Feedback type="invalid">
                        Seleccione qué desea inspeccionar
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Row>
              )}
              
              {/* Campos de formulario condicionados */}
              {(isEditMode || inspectionType) && (
                <>
                  {/* Tipo específico de inspección y fecha */}
                  <Row className="mb-3">
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Tipo <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        isInvalid={errors.type}
                        disabled={loading || isEditMode}
                      >
                        <option value="">Seleccione</option>
                        {getInspectionTypes().map((tipo) => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.descripcion}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.type && (
                        <Form.Control.Feedback type="invalid">
                          Seleccione un tipo de inspección
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                    
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Fecha <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        isInvalid={errors.date}
                        disabled={loading}
                      />
                      {errors.date && (
                        <Form.Control.Feedback type="invalid">
                          Ingrese una fecha válida
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Row>
                  
                  {/* Radio buttons para tipo de dueño */}
                  {inspectionType === 'tool' && !isEditMode && (
                    <Row className="mb-3">
                      <Col xs={12}>
                        <Form.Label>Asignado a <span className="text-danger">*</span></Form.Label>
                        <div className="d-flex">
                          <Form.Check
                            type="radio"
                            id="asignado-persona"
                            name="asignado"
                            label="Persona"
                            value="person"
                            checked={ownerType === 'person'}
                            onChange={handleOwnerTypeChange}
                            className="me-4"
                            disabled={loading}
                          />
                          <Form.Check
                            type="radio"
                            id="asignado-obra"
                            name="asignado"
                            label="Obra"
                            value="work"
                            checked={ownerType === 'work'}
                            onChange={handleOwnerTypeChange}
                            disabled={loading}
                          />
                        </div>
                      </Col>
                    </Row>
                  )}
                  
                  {/* Selección de persona/obra (dueño) - SOLO si no es el caso 2c */}
                  {!(inspectionType === 'tool' && selectedTool && !selectedOwner && isEditMode) && (
                    <Row className="mb-3">
                      <Col xs={12}>
                        <SearchableDropdown
                          items={ownerType === 'person' ? people : works}
                          label={ownerType === 'person' ? 'Persona' : 'Obra'}
                          value={selectedOwner}
                          onChange={handleOwnerSelect}
                          placeholder={`Buscar ${ownerType === 'person' ? 'persona' : 'obra'}...`}
                          disabled={loading || isEditMode}
                          isInvalid={errors.owner}
                          required={!isEditMode || !(inspectionType === 'tool' && selectedTool && !selectedOwner)}
                        />
                        {errors.owner && (
                          <div className="text-danger mt-1 small">
                            Debe seleccionar {ownerType === 'person' ? 'una persona' : 'una obra'}
                          </div>
                        )}
                      </Col>
                    </Row>
                  )}
                  
                  {/* Mostrar información cuando la herramienta no tiene asignación específica */}
                  {inspectionType === 'tool' && selectedTool && !selectedOwner && isEditMode && (
                    <Row className="mb-3">
                      <Col xs={12}>
                        <div className="alert alert-info">
                          <strong>Herramienta sin asignación específica:</strong><br/>
                          {selectedTool.tool?.name || 'Herramienta'} 
                          {selectedTool.serial && ` - Serial: ${selectedTool.serial}`}
                        </div>
                      </Col>
                    </Row>
                  )}
                  
                  {/* Selección de herramienta */}
                  {inspectionType === 'tool' && (selectedOwner || (isEditMode && selectedTool)) && (
                    <Row className="mb-3">
                      <Col xs={12}>
                        <SearchableDropdown
                          items={tools}
                          label="Herramienta"
                          value={selectedTool}
                          onChange={handleToolSelect}
                          placeholder="Buscar herramienta..."
                          disabled={loading || isEditMode}
                          isInvalid={errors.tool}
                          required={true}
                        />
                        {errors.tool && (
                          <div className="text-danger mt-1 small">
                            Debe seleccionar una herramienta
                          </div>
                        )}
                      </Col>
                    </Row>
                  )}
                  
                  {/* Responsable y Estado */}
                  <Row className="mb-3">
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Responsable <span className="text-danger">*</span></Form.Label>
                      {isCreatingNewResponsable ? (
                        <InputGroup>
                          <Form.Control
                            type="text"
                            name="responsable"
                            value={formData.responsable}
                            onChange={handleInputChange}
                            isInvalid={errors.responsable}
                            placeholder="Escriba el nuevo responsable"
                            disabled={loading}
                            autoFocus
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              setIsCreatingNewResponsable(false);
                              setFormData(prev => ({ ...prev, responsable: '' }));
                            }}
                            disabled={loading}
                          >
                            Cancelar
                          </Button>
                        </InputGroup>
                      ) : (
                        <Form.Select
                          name="responsable"
                          value={formData.responsable}
                          onChange={handleResponsableSelection}
                          isInvalid={errors.responsable}
                          disabled={loadingResponsables || loading}
                        >
                          <option value="">{loadingResponsables ? 'Cargando opciones...' : 'Seleccione de la lista...'}</option>
                          {responsableOptions.map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                          <option value={NEW_RESPONSABLE_OPTION_VALUE} style={{ fontStyle: 'italic', color: '#0d6efd' }}>
                            {NEW_RESPONSABLE_LABEL}
                          </option>
                        </Form.Select>
                      )}
                      {errors.responsable && (
                        <Form.Control.Feedback type="invalid">
                          Ingrese el nombre del responsable
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                    
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Estado</Form.Label>
                      <Form.Control
                        type="text"
                        name="toolStatus"
                        value={formData.toolStatus}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Form.Group>
                  </Row>
                  
                  {/* Observaciones */}
                  <Form.Group className="mb-3">
                    <Form.Label>Observaciones <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="observations"
                      value={formData.observations}
                      onChange={handleInputChange}
                      isInvalid={errors.observations}
                      disabled={loading}
                    />
                    {errors.observations && (
                      <Form.Control.Feedback type="invalid">
                        Ingrese observaciones sobre la inspección
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  
                  {/* Finalizado */}
                  {isEditMode && (
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="finalizado"
                        name="finished"
                        label="Finalizado"
                        checked={formData.finished}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Form.Group>
                  )}
                </>
              )}
              
              {/* Botones de acción */}
              <div className="d-flex justify-content-end mt-4">
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  className="me-2"
                  disabled={loading}
                  type="button"
                >
                  <FaArrowLeft className="me-1" /> Volver
                </Button>
                <Button
                  variant="success"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-1" /> Guardar
                    </>
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
      
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
      
      <style jsx="true">{`
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </Container>
  );
}

export default InspectionForm;